import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import DigitalSignaturePad from './SignaturePad';
import SimpleSignaturePad from './SimpleSignaturePad';
import VehicleHandoverProtocol from './VehicleHandoverProtocol';
import {
  Truck,
  Plus,
  Edit,
  Trash2,
  ArrowRightLeft,
  ArrowLeftRight,
  Fuel,
  Gauge,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  PenTool,
  Camera,
  RefreshCw,
  FileText,
  Key,
  Download,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Fleet = () => {
  const [vehicles, setVehicles] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehicles');
  
  // Dialog states
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [showHandoverDialog, setShowHandoverDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [importFile, setImportFile] = useState(null);
  
  // Form states
  const [vehicleForm, setVehicleForm] = useState({
    license_plate: '',
    vehicle_type: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    mileage: 0,
    fuel_level: 100,
    notes: ''
  });
  
  const [handoverForm, setHandoverForm] = useState({
    vehicle_id: '',
    employee_id: '',
    mileage_start: 0,
    fuel_level_start: 100,
    tools_assigned: [],
    damage_notes: '',
    photos_handover: [],
    signature_handover: ''
  });
  
  const [returnForm, setReturnForm] = useState({
    handover_id: '',
    mileage_end: 0,
    fuel_level_end: 100,
    tools_returned: [],
    new_damage_notes: '',
    photos_return: [],
    signature_return: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, handoversRes, employeesRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/vehicle-handovers`),
        axios.get(`${API}/employees`)
      ]);
      
      setVehicles(vehiclesRes.data);
      setHandovers(handoversRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching fleet data:', error);
      toast.error('Fehler beim Laden der Fuhrpark-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, vehicleForm);
        toast.success('Fahrzeug erfolgreich aktualisiert');
      } else {
        await axios.post(`${API}/vehicles`, vehicleForm);
        toast.success('Fahrzeug erfolgreich hinzugefügt');
      }
      
      fetchData();
      setShowVehicleDialog(false);
      resetVehicleForm();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern des Fahrzeugs');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHandoverSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/vehicle-handovers`, handoverForm);
      toast.success('Fahrzeugübergabe erfolgreich dokumentiert');
      
      fetchData();
      setShowHandoverDialog(false);
      resetHandoverForm();
    } catch (error) {
      console.error('Error creating handover:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Fahrzeugübergabe');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/vehicle-returns`, returnForm);
      toast.success('Fahrzeugrückgabe erfolgreich dokumentiert');
      
      fetchData();
      setShowReturnDialog(false);
      resetReturnForm();
    } catch (error) {
      console.error('Error creating return:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Fahrzeugrückgabe');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId, licensePlate) => {
    if (window.confirm(`Sind Sie sicher, dass Sie das Fahrzeug ${licensePlate} löschen möchten?`)) {
      try {
        await axios.delete(`${API}/vehicles/${vehicleId}`);
        toast.success('Fahrzeug erfolgreich gelöscht');
        fetchData();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        toast.error('Fehler beim Löschen des Fahrzeugs');
      }
    }
  };

  const resetVehicleForm = () => {
    setVehicleForm({
      license_plate: '',
      vehicle_type: '',
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      mileage: 0,
      fuel_level: 100,
      notes: ''
    });
    setEditingVehicle(null);
  };

  const resetHandoverForm = () => {
    setHandoverForm({
      vehicle_id: '',
      employee_id: '',
      mileage_start: 0,
      fuel_level_start: 100,
      tools_assigned: [],
      damage_notes: '',
      photos_handover: [],
      signature_handover: ''
    });
  };

  const resetReturnForm = () => {
    setReturnForm({
      handover_id: '',
      mileage_end: 0,
      fuel_level_end: 100,
      tools_returned: [],
      new_damage_notes: '',
      photos_return: [],
      signature_return: ''
    });
    setSelectedHandover(null);
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/vehicles/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fahrzeuge_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Fehler beim Excel-Export');
    }
  };

  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/vehicles/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fahrzeuge_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Fehler beim PDF-Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API}/vehicles/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Import erfolgreich: ${response.data.imported} neu, ${response.data.updated} aktualisiert`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
        toast.warning(`${response.data.errors.length} Fehler beim Import`);
      }

      setShowImportDialog(false);
      setImportFile(null);
      fetchData();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      verfügbar: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      im_einsatz: 'bg-blue-100 text-blue-800 border-blue-300',
      wartung: 'bg-amber-100 text-amber-800 border-amber-300',
      defekt: 'bg-red-100 text-red-800 border-red-300'
    };
    return variants[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status) => {
    const icons = {
      verfügbar: <CheckCircle className="w-4 h-4" />,
      im_einsatz: <Clock className="w-4 h-4" />,
      wartung: <AlertTriangle className="w-4 h-4" />,
      defekt: <AlertTriangle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unbekannt';
  };

  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.brand} ${vehicle.model} (${vehicle.license_plate})` : 'Unbekannt';
  };

  const availableVehicles = vehicles.filter(v => v.status === 'verfügbar');
  const activeHandovers = handovers.filter(h => h.status === 'laufend' || h.status === 'ausstehend');
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Lade Fuhrpark-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="fleet-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Truck className="w-8 h-8 text-emerald-600" />
              Fuhrpark 🚐
            </h1>
            <p className="text-slate-600 mt-1">Fahrzeugverwaltung mit digitaler Signatur für Übergabe/Rückgabe</p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportExcel}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Excel
            </Button>
            <Button
              variant="outline"
              onClick={exportPDF}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              PDF
            </Button>
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Upload className="w-4 h-4" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Fahrzeuge importieren</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Excel-Datei auswählen
                    </label>
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setImportFile(e.target.files[0])}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Spalten: Kennzeichen, Typ, Marke, Modell, Jahr, Farbe, Kilometerstand, Status, Kraftstoffstand (%), Notizen
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={handleImport} disabled={!importFile}>
                      <Upload className="w-4 h-4 mr-2" />
                      Importieren
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              onClick={fetchData}
              className="gap-2"
              data-testid="refresh-fleet-button"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      {/* Fleet Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="modern-card border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Gesamt Fahrzeuge</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-vehicles-stat">
                  {vehicles.length}
                </p>
              </div>
              <Truck className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-emerald-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Verfügbar</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="available-vehicles-stat">
                  {availableVehicles.length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-amber-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Im Einsatz</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="in-use-vehicles-stat">
                  {vehicles.filter(v => v.status === 'im_einsatz').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Aktive Übergaben</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="active-handovers-stat">
                  {activeHandovers.length}
                </p>
              </div>
              <ArrowRightLeft className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="vehicles" className="gap-2">
            <Truck className="w-4 h-4" />
            Fahrzeuge
          </TabsTrigger>
          <TabsTrigger value="handovers" className="gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Übergaben
          </TabsTrigger>
          <TabsTrigger value="returns" className="gap-2">
            <ArrowLeftRight className="w-4 h-4" />
            Rückgaben
          </TabsTrigger>
          <TabsTrigger value="protocols" className="gap-2">
            <FileText className="w-4 h-4" />
            KFZ-Protokolle
          </TabsTrigger>
        </TabsList>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles" className="mt-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-slate-900">Fahrzeugverwaltung</h2>
            <Dialog open={showVehicleDialog} onOpenChange={setShowVehicleDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={resetVehicleForm} data-testid="add-vehicle-button">
                  <Plus className="w-4 h-4" />
                  Fahrzeug hinzufügen
                </Button>
              </DialogTrigger>
              <VehicleForm
                formData={vehicleForm}
                setFormData={setVehicleForm}
                onSubmit={handleVehicleSubmit}
                submitting={submitting}
                isEdit={!!editingVehicle}
              />
            </Dialog>
          </div>

          {vehicles.length === 0 ? (
            <Card className="modern-card text-center py-12">
              <CardContent>
                <Truck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Fahrzeuge gefunden</h3>
                <p className="text-slate-600 mb-4">Fügen Sie Ihr erstes Fahrzeug hinzu.</p>
                <Button className="gap-2" onClick={() => setShowVehicleDialog(true)} data-testid="empty-state-add-vehicle">
                  <Plus className="w-4 h-4" />
                  Erstes Fahrzeug hinzufügen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  onEdit={(vehicle) => {
                    setEditingVehicle(vehicle);
                    setVehicleForm({ ...vehicle });
                    setShowVehicleDialog(true);
                  }}
                  onDelete={handleDeleteVehicle}
                  onHandover={(vehicle) => {
                    const newHandoverForm = { 
                      ...handoverForm, 
                      vehicle_id: vehicle.id, 
                      mileage_start: vehicle.mileage || 0, 
                      fuel_level_start: vehicle.fuel_level || 100 
                    };
                    setHandoverForm(newHandoverForm);
                    setShowHandoverDialog(true);
                  }}
                  getStatusBadge={getStatusBadge}
                  getStatusIcon={getStatusIcon}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Handovers Tab */}
        <TabsContent value="handovers" className="mt-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-slate-900">Fahrzeugübergaben</h2>
            <Dialog open={showHandoverDialog} onOpenChange={setShowHandoverDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={resetHandoverForm} data-testid="create-handover-button">
                  <Key className="w-4 h-4" />
                  Übergabe erstellen
                </Button>
              </DialogTrigger>
              <HandoverForm
                formData={handoverForm}
                setFormData={setHandoverForm}
                onSubmit={handleHandoverSubmit}
                submitting={submitting}
                vehicles={availableVehicles}
                employees={employees}
              />
            </Dialog>
          </div>

          <HandoversList 
            handovers={handovers}
            getVehicleName={getVehicleName}
            getEmployeeName={getEmployeeName}
            onReturn={(handover) => {
              setSelectedHandover(handover);
              setReturnForm({ 
                ...returnForm, 
                handover_id: handover.id,
                mileage_end: handover.mileage_start,
                fuel_level_end: handover.fuel_level_start
              });
              setShowReturnDialog(true);
            }}
          />
        </TabsContent>

        {/* Returns Tab */}
        <TabsContent value="returns" className="mt-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-6">Fahrzeugrückgaben</h2>
          <ReturnsList />
        </TabsContent>

        {/* KFZ Protocols Tab */}
        <TabsContent value="protocols" className="mt-6">
          <VehicleHandoverProtocol 
            onBack={() => setActiveTab('vehicles')}
          />
        </TabsContent>
      </Tabs>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <ReturnForm
          formData={returnForm}
          setFormData={setReturnForm}
          onSubmit={handleReturnSubmit}
          submitting={submitting}
          handover={selectedHandover}
          getVehicleName={getVehicleName}
          getEmployeeName={getEmployeeName}
        />
      </Dialog>
    </div>
  );
};

// Vehicle Card Component
const VehicleCard = ({ vehicle, onEdit, onDelete, onHandover, getStatusBadge, getStatusIcon }) => {
  const handleHandoverClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onHandover) {
      onHandover(vehicle);
    }
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit(vehicle);
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(vehicle.id, vehicle.license_plate);
  };

  return (
    <Card className="modern-card hover:shadow-lg transition-all duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{vehicle.license_plate}</CardTitle>
            <p className="text-sm text-slate-600">{vehicle.brand} {vehicle.model} ({vehicle.year})</p>
          </div>
          
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleEditClick} className="p-2" data-testid={`edit-vehicle-${vehicle.id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleDeleteClick}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-vehicle-${vehicle.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Status:</span>
          <Badge className={`${getStatusBadge(vehicle.status)} border gap-1`}>
            {getStatusIcon(vehicle.status)}
            {vehicle.status.replace('_', ' ')}
          </Badge>
        </div>

        {/* Vehicle Info */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Typ:</span>
            <span className="font-medium">{vehicle.vehicle_type ? vehicle.vehicle_type.toUpperCase() : 'N/A'}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Farbe:</span>
            <span>{vehicle.color || 'N/A'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Gauge className="w-4 h-4 text-slate-500" />
            <span>{vehicle.mileage ? vehicle.mileage.toLocaleString() : '0'} km</span>
          </div>
          {vehicle.fuel_level !== undefined && vehicle.fuel_level !== null && (
            <div className="flex items-center gap-2 text-sm">
              <Fuel className="w-4 h-4 text-slate-500" />
              <span>{vehicle.fuel_level}% Tank</span>
            </div>
          )}
        </div>

        {/* Maintenance Info */}
        {vehicle.next_maintenance && (
          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            <Calendar className="w-3 h-3 inline mr-1" />
            Nächste Wartung: {new Date(vehicle.next_maintenance).toLocaleDateString('de-DE')}
          </div>
        )}

        {/* Actions */}
        {vehicle.status === 'verfügbar' && (
          <Button 
            type="button"
            className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={handleHandoverClick}
            data-testid={`handover-vehicle-${vehicle.id}`}
          >
            <Key className="w-4 h-4" />
            Übergeben
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Vehicle Form Component
const VehicleForm = ({ formData, setFormData, onSubmit, submitting, isEdit }) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  return (
    <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>
          {isEdit ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug hinzufügen'}
        </DialogTitle>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Kennzeichen</label>
          <Input
            value={formData.license_plate}
            onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
            placeholder="B-SK 1234"
            required
            data-testid="license-plate-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Fahrzeugtyp</label>
            <Select value={formData.vehicle_type} onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}>
              <SelectTrigger data-testid="vehicle-type-select">
                <SelectValue placeholder="Wählen Sie..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pkw">PKW</SelectItem>
                <SelectItem value="transporter">Transporter</SelectItem>
                <SelectItem value="lkw">LKW</SelectItem>
                <SelectItem value="motorrad">Motorrad</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Baujahr</label>
            <Select value={formData.year.toString()} onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}>
              <SelectTrigger data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Marke</label>
            <Input
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              placeholder="Mercedes-Benz"
              required
              data-testid="brand-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Modell</label>
            <Input
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              placeholder="Sprinter"
              required
              data-testid="model-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Farbe</label>
            <Input
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              placeholder="Weiß"
              required
              data-testid="color-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Kilometerstand</label>
            <Input
              type="number"
              value={formData.mileage}
              onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) || 0 })}
              placeholder="0"
              min="0"
              data-testid="mileage-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Tankfüllung (%)</label>
          <Input
            type="number"
            value={formData.fuel_level}
            onChange={(e) => setFormData({ ...formData, fuel_level: parseInt(e.target.value) || 0 })}
            min="0"
            max="100"
            data-testid="fuel-level-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notizen (optional)</label>
          <Input
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Zusätzliche Informationen..."
            data-testid="notes-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-4" style={{ zIndex: 9999 }}>
          <Button 
            type="button" 
            variant="outline" 
            disabled={submitting}
            onClick={() => setShowVehicleDialog(false)}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            disabled={submitting} 
            data-testid="save-vehicle-button"
            style={{ zIndex: 10000 }}
          >
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Speichere...
              </>
            ) : (
              isEdit ? 'Aktualisieren' : 'Hinzufügen'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// Handover Form Component
const HandoverForm = ({ formData, setFormData, onSubmit, submitting, vehicles, employees }) => {
  return (
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Key className="w-5 h-5 text-blue-600" />
          Fahrzeugübergabe
        </DialogTitle>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Fahrzeug</label>
          <Select value={formData.vehicle_id} onValueChange={(value) => {
            const vehicle = vehicles.find(v => v.id === value);
            setFormData({ 
              ...formData, 
              vehicle_id: value,
              mileage_start: vehicle?.mileage || 0,
              fuel_level_start: vehicle?.fuel_level || 100
            });
          }}>
            <SelectTrigger data-testid="handover-vehicle-select">
              <SelectValue placeholder="Fahrzeug auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {vehicles.map(vehicle => (
                <SelectItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.license_plate} - {vehicle.brand} {vehicle.model}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Mitarbeiter</label>
          <Select value={formData.employee_id} onValueChange={(value) => setFormData({ ...formData, employee_id: value })}>
            <SelectTrigger data-testid="handover-employee-select">
              <SelectValue placeholder="Mitarbeiter auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {employees.filter(emp => emp.status === 'aktiv').map(employee => (
                <SelectItem key={employee.id} value={employee.id}>
                  {employee.first_name} {employee.last_name} (#{employee.employee_number})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Kilometerstand</label>
            <Input
              type="number"
              value={formData.mileage_start}
              onChange={(e) => setFormData({ ...formData, mileage_start: parseInt(e.target.value) || 0 })}
              min="0"
              required
              data-testid="handover-mileage-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tankfüllung (%)</label>
            <Input
              type="number"
              value={formData.fuel_level_start}
              onChange={(e) => setFormData({ ...formData, fuel_level_start: parseInt(e.target.value) || 0 })}
              min="0"
              max="100"
              required
              data-testid="handover-fuel-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Schadensnotizen (optional)</label>
          <Input
            value={formData.damage_notes}
            onChange={(e) => setFormData({ ...formData, damage_notes: e.target.value })}
            placeholder="Bereits vorhandene Schäden dokumentieren..."
            data-testid="handover-damage-notes"
          />
        </div>

        {/* Digital Signature */}
        <div>
          <label className="block text-sm font-medium mb-2">Digitale Signatur</label>
          <div className="space-y-4">
            <DigitalSignaturePad
              onSignatureChange={(signature) => setFormData({ ...formData, signature_handover: signature })}
              initialSignature={formData.signature_handover}
            />
            
            {/* Fallback Simple Signature */}
            <div className="border-t pt-4">
              <p className="text-sm text-slate-600 mb-2">Alternative (falls oben nicht funktioniert):</p>
              <SimpleSignaturePad
                onSignatureChange={(signature) => setFormData({ ...formData, signature_handover: signature })}
                initialSignature={formData.signature_handover}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4" style={{ zIndex: 9999 }}>
          <Button 
            type="button" 
            variant="outline" 
            disabled={submitting}
            onClick={() => setShowHandoverDialog(false)}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            disabled={submitting || !formData.signature_handover} 
            data-testid="save-handover-button"
            style={{ zIndex: 10000 }}
          >
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Übergabe dokumentieren...
              </>
            ) : (
              'Übergabe dokumentieren'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// Return Form Component
const ReturnForm = ({ formData, setFormData, onSubmit, submitting, handover, getVehicleName, getEmployeeName }) => {
  if (!handover) return null;

  return (
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-green-600" />
          Fahrzeugrückgabe
        </DialogTitle>
      </DialogHeader>
      
      <div className="mb-4 p-3 bg-slate-50 rounded-lg">
        <p className="text-sm"><strong>Fahrzeug:</strong> {getVehicleName(handover.vehicle_id)}</p>
        <p className="text-sm"><strong>Mitarbeiter:</strong> {getEmployeeName(handover.employee_id)}</p>
        <p className="text-sm"><strong>Übergabe am:</strong> {new Date(handover.handover_date).toLocaleDateString('de-DE')}</p>
      </div>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Kilometerstand Rückgabe</label>
            <Input
              type="number"
              value={formData.mileage_end}
              onChange={(e) => setFormData({ ...formData, mileage_end: parseInt(e.target.value) || 0 })}
              min={handover.mileage_start}
              required
              data-testid="return-mileage-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Bei Übergabe: {handover.mileage_start?.toLocaleString()} km
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tankfüllung (%)</label>
            <Input
              type="number"
              value={formData.fuel_level_end}
              onChange={(e) => setFormData({ ...formData, fuel_level_end: parseInt(e.target.value) || 0 })}
              min="0"
              max="100"
              required
              data-testid="return-fuel-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Bei Übergabe: {handover.fuel_level_start}%
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Neue Schäden (optional)</label>
          <Input
            value={formData.new_damage_notes}
            onChange={(e) => setFormData({ ...formData, new_damage_notes: e.target.value })}
            placeholder="Neue Schäden dokumentieren..."
            data-testid="return-damage-notes"
          />
        </div>

        {/* Digital Signature */}
        <div>
          <label className="block text-sm font-medium mb-2">Rückgabe-Signatur</label>
          <div className="space-y-4">
            <DigitalSignaturePad
              onSignatureChange={(signature) => setFormData({ ...formData, signature_return: signature })}
              initialSignature={formData.signature_return}
            />
            
            {/* Fallback Simple Signature */}
            <div className="border-t pt-4">
              <p className="text-sm text-slate-600 mb-2">Alternative (falls oben nicht funktioniert):</p>
              <SimpleSignaturePad
                onSignatureChange={(signature) => setFormData({ ...formData, signature_return: signature })}
                initialSignature={formData.signature_return}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4" style={{ zIndex: 9999 }}>
          <Button 
            type="button" 
            variant="outline" 
            disabled={submitting}
            onClick={() => setShowReturnDialog(false)}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            disabled={submitting || !formData.signature_return} 
            data-testid="save-return-button"
            style={{ zIndex: 10000 }}
          >
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Rückgabe dokumentieren...
              </>
            ) : (
              'Rückgabe dokumentieren'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// Handovers List Component
const HandoversList = ({ handovers, getVehicleName, getEmployeeName, onReturn }) => {
  const activeHandovers = handovers.filter(h => h.status === 'laufend' || h.status === 'ausstehend');

  if (activeHandovers.length === 0) {
    return (
      <Card className="modern-card text-center py-12">
        <CardContent>
          <Key className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine aktiven Übergaben</h3>
          <p className="text-slate-600">Erstellen Sie eine neue Fahrzeugübergabe.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {activeHandovers.map((handover) => (
        <Card key={handover.id} className="modern-card">
          <CardHeader>
            <CardTitle className="text-lg">{getVehicleName(handover.vehicle_id)}</CardTitle>
            <p className="text-sm text-slate-600">
              Übergabe an: {getEmployeeName(handover.employee_id)}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Übergabe am:</span>
                <span>{new Date(handover.handover_date).toLocaleDateString('de-DE')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Kilometerstand:</span>
                <span>{handover.mileage_start?.toLocaleString()} km</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Tankfüllung:</span>
                <span>{handover.fuel_level_start}%</span>
              </div>
            </div>
            
            {handover.damage_notes && (
              <div className="text-xs bg-amber-50 p-2 rounded border border-amber-200">
                <strong>Schäden:</strong> {handover.damage_notes}
              </div>
            )}
            
            <Button
              className="w-full gap-2 bg-green-600 hover:bg-green-700"
              onClick={() => onReturn(handover)}
              data-testid={`return-vehicle-${handover.id}`}
            >
              <ArrowLeftRight className="w-4 h-4" />
              Rückgabe dokumentieren
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Returns List Component
const ReturnsList = () => {
  return (
    <Card className="modern-card text-center py-12">
      <CardContent>
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Rückgabe-Protokolle</h3>
        <p className="text-slate-600">Hier werden alle dokumentierten Fahrzeugrückgaben angezeigt.</p>
      </CardContent>
    </Card>
  );
};

export default Fleet;